import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Input, Modal, Select } from 'antd';
import { LanguagePicker } from '@shared/components/LanguagePicker/LanguagePicker';
import { pickDefaultLanguage, useActiveLanguages } from '@features/languages/useLanguages';
import {
  MENU_LOCATIONS,
  type MenuCreateInput,
  type MenuListItem,
  type MenuUpdateInput,
} from '../types';

interface FormValues {
  name: string;
  location: string;
  languageId?: string;
}

interface MenuFormModalProps {
  open: boolean;
  editing: MenuListItem | null;
  saving: boolean;
  onCancel: () => void;
  onCreate: (input: MenuCreateInput) => void;
  onUpdate: (id: string, input: MenuUpdateInput) => void;
}

/**
 * Menü meta formu (ad/konum/dil). Dil yalnızca create'te seçilir (config-tier
 * satır-başına-dil, Category hafif deseni); düzenlemede ad + konum değişir.
 */
export function MenuFormModal({
  open,
  editing,
  saving,
  onCancel,
  onCreate,
  onUpdate,
}: MenuFormModalProps) {
  const { t } = useTranslation(['menus', 'common']);
  const td = t as unknown as (key: string) => string;
  const [form] = Form.useForm<FormValues>();
  const isEdit = editing !== null;
  const { data: activeLanguages = [] } = useActiveLanguages();

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      name: editing?.name ?? '',
      location: editing?.location ?? MENU_LOCATIONS[0],
      languageId: editing ? undefined : pickDefaultLanguage(activeLanguages)?.id,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing, activeLanguages]);

  const onOk = () => {
    void (async () => {
      try {
        const values = await form.validateFields();
        if (isEdit && editing) {
          onUpdate(editing.id, { name: values.name.trim(), location: values.location });
        } else {
          onCreate({
            name: values.name.trim(),
            location: values.location,
            languageId: values.languageId ?? null,
          });
        }
      } catch {
        // inline validation surfaced by AntD
      }
    })();
  };

  return (
    <Modal
      open={open}
      title={isEdit ? t('form.editTitle') : t('form.createTitle')}
      onCancel={onCancel}
      onOk={onOk}
      okText={isEdit ? t('common:actions.save') : t('common:actions.create')}
      cancelText={t('common:actions.cancel')}
      confirmLoading={saving}
      destroyOnClose
      maskClosable={!saving}
    >
      <Form<FormValues> form={form} layout="vertical">
        <Form.Item
          name="name"
          label={t('form.name')}
          rules={[{ required: true, message: t('form.nameRequired') }]}
        >
          <Input placeholder={t('form.namePlaceholder')} maxLength={120} />
        </Form.Item>
        <Form.Item
          name="location"
          label={t('form.location')}
          rules={[{ required: true, message: t('form.locationRequired') }]}
        >
          <Select
            options={MENU_LOCATIONS.map((loc) => ({ value: loc, label: td(`location.${loc}`) }))}
          />
        </Form.Item>
        {!isEdit ? (
          <Form.Item label={t('common:contentLanguage.label')} help={t('form.languageHint')}>
            <Form.Item name="languageId" noStyle>
              <LanguagePickerField />
            </Form.Item>
          </Form.Item>
        ) : null}
      </Form>
    </Modal>
  );
}

/** AntD Form.Item value/onChange köprüsü — LanguagePicker'ı forma bağlar. */
function LanguagePickerField({
  value,
  onChange,
}: {
  value?: string;
  onChange?: (v: string | undefined) => void;
}) {
  return <LanguagePicker value={value} onChange={(v) => onChange?.(v)} style={{ width: '100%' }} />;
}
